import {
  Box,
  Button,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useState } from 'react';
import { FiCalendar, FiChevronDown, FiPhone, FiX } from 'react-icons/fi';
import { Trans } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { useDisclosure } from '@hooks/useDisclosure';
import logo from '@assets/logo512.png';
import { ToggleButton } from './ToggleButton';
import {
  navbarButtons,
  navbarRightLinks,
  services,
  CONTACT_INFO,
} from './NavbarNavigateOptions';
import LanguageMenu from '../../menu/LanguageMenu';
import {
  NAVBAR_CTA_BOOK_CONSULTATION,
  NAVBAR_CTA_CALL_NOW,
  NAVBAR_LINK_SERVICES,
  NAVBAR_TAGLINE,
} from '../../../locales/types';

const linkItems = [...navbarButtons, ...navbarRightLinks];

export const MobileDrawer = () => {
  const { isOpen, onToggle, onClose } = useDisclosure();
  const [servicesOpen, setServicesOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const go = (href) => {
    navigate(href);
    onClose();
  };

  const isActive = (href) => {
    const clean = (href || '/').split('#')[0];
    return clean === '/' ? pathname === '/' : pathname.startsWith(clean);
  };

  return (
    <>
      <ToggleButton isOpen={isOpen} onClick={onToggle} aria-label="Open menu" />
      <Drawer
        anchor="left"
        open={isOpen}
        onClose={onClose}
        slotProps={{
          paper: {
            sx: {
              width: { xs: '85vw', sm: 360 },
              maxWidth: 400,
              bgcolor: 'background.paper',
            },
          },
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ p: 2, bgcolor: 'primary.main', color: 'common.white' }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              component="img"
              src={logo}
              alt="Linares Multiservices"
              sx={{ width: 40, height: 40, objectFit: 'contain' }}
            />
            <Stack spacing={-0.25}>
              <Typography sx={{ fontWeight: 800, fontSize: '1rem', lineHeight: 1.1 }}>
                Linares
              </Typography>
              <Typography
                sx={{ fontSize: 10, letterSpacing: 1.4, textTransform: 'uppercase', opacity: 0.85 }}
              >
                Multiservices
              </Typography>
            </Stack>
          </Stack>
          <IconButton onClick={onClose} sx={{ color: 'common.white' }} aria-label="Close menu">
            <FiX />
          </IconButton>
        </Stack>

        <Typography
          variant="caption"
          sx={{ px: 2, py: 1.5, color: 'text.secondary', display: 'block' }}
        >
          <Trans i18nKey={`component.navbar.${NAVBAR_TAGLINE}`} />
        </Typography>

        <Divider />

        {/* Primary links */}
        <List sx={{ py: 1 }}>
          {linkItems.map((item) => (
            <ListItemButton
              key={item.title}
              selected={isActive(item.href)}
              onClick={() => go(item.href)}
              sx={{
                borderRadius: 1,
                mx: 1,
                '&.Mui-selected': {
                  bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
                  color: 'primary.main',
                  fontWeight: 700,
                },
              }}
            >
              <ListItemText
                primary={<Trans i18nKey={`component.navbar.${item.trans}`} />}
                primaryTypographyProps={{ fontWeight: isActive(item.href) ? 700 : 500 }}
              />
            </ListItemButton>
          ))}

          {/* Services collapsible */}
          <ListItemButton
            onClick={() => setServicesOpen((v) => !v)}
            sx={{ borderRadius: 1, mx: 1 }}
            aria-expanded={servicesOpen}
          >
            <ListItemText
              primary={<Trans i18nKey={`component.navbar.${NAVBAR_LINK_SERVICES}`} />}
              primaryTypographyProps={{ fontWeight: 500 }}
            />
            <Box
              sx={{
                display: 'inline-flex',
                transition: 'transform .2s ease',
                transform: servicesOpen ? 'rotate(180deg)' : 'none',
              }}
            >
              <FiChevronDown />
            </Box>
          </ListItemButton>
          <Collapse in={servicesOpen} unmountOnExit>
            <List disablePadding>
              {services.map((item) => {
                const Icon = item.icon;
                return (
                  <ListItemButton
                    key={item.title}
                    onClick={() => go(item.href)}
                    selected={isActive(item.href)}
                    sx={{ pl: 4, mx: 1, borderRadius: 1 }}
                  >
                    <ListItemIcon sx={{ minWidth: 36, color: 'primary.main' }}>
                      {Icon && <Icon size={20} />}
                    </ListItemIcon>
                    <ListItemText
                      primary={<Trans i18nKey={`component.navbar.${item.trans}`} />}
                      primaryTypographyProps={{ fontSize: '0.92rem' }}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </Collapse>
        </List>

        <Box sx={{ flexGrow: 1 }} />

        <Divider />

        {/* Footer CTAs */}
        <Stack spacing={1.25} sx={{ p: 2 }}>
          <Button
            fullWidth
            variant="contained"
            color="secondary"
            startIcon={<FiCalendar />}
            onClick={() => go('/support')}
            sx={{ fontWeight: 700, textTransform: 'none', borderRadius: 999, py: 1.1 }}
          >
            <Trans i18nKey={`component.navbar.${NAVBAR_CTA_BOOK_CONSULTATION}`} />
          </Button>
          <Button
            fullWidth
            variant="outlined"
            color="primary"
            component="a"
            href={CONTACT_INFO.phoneHref}
            startIcon={<FiPhone />}
            sx={{ fontWeight: 600, textTransform: 'none', borderRadius: 999, py: 1.1 }}
          >
            <Trans i18nKey={`component.navbar.${NAVBAR_CTA_CALL_NOW}`} /> · {CONTACT_INFO.phone}
          </Button>
          <Box sx={{ pt: 0.5, display: 'flex', justifyContent: 'center' }}>
            <LanguageMenu />
          </Box>
        </Stack>
      </Drawer>
    </>
  );
};
